/**
 * Phase 3a — Passkey credential 삭제 직전 master password 재인증 모달 (Refs: #1112).
 *
 * D4 결정: 사용자가 master pw를 모르면 삭제 불가 (도난 디바이스에서의 비인가 삭제 차단).
 * 디자인: Figma 685-2322 — 빨간 ! 아이콘 좌측 + 타이틀/본문/입력/footer 패턴.
 */
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Button, Input, Modal } from 'antd'
import { useTranslation } from 'react-i18next'
import { hashString } from 'pUtils'
import { verifyMasterPw, deleteCredential } from 'services/passkey'
import { errorBase } from 'utils/ui'

import exclamationCircleRed22 from '@/assets/images/exclamation-circle-red-22.svg'
import styles from '../style.module.scss'

const PasskeyDeleteConfirmModal = ({
  open = false,
  credentialId = null,
  onClose = () => {},
  onDeleted = () => {},
}) => {
  const { t } = useTranslation()
  const [masterPw, setMasterPw] = useState('')
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!open) return
    setMasterPw('')
    setProcessing(false)
    setError(null)
  }, [open])

  const handleDelete = async () => {
    if (!masterPw) {
      setError(t('passkey.error.generic'))
      return
    }
    setProcessing(true)
    setError(null)
    try {
      const hmk = hashString(masterPw)
      try {
        await verifyMasterPw(hmk)
      } catch (verifyErr) {
        // SAFEBOX_838 master pw mismatch → 전용 한글 메시지, 그 외는 generic.
        // FIDO/Corbado 권고: 백엔드 native message 를 사용자에게 직접 노출하지 않음.
        if (verifyErr?.code === 838) {
          setError(t('passkey.error.masterPwMismatch'))
        } else {
          setError(t('passkey.error.generic'))
        }
        setProcessing(false)
        return
      }
      await deleteCredential(credentialId)
      // 서버 삭제 성공 — 이후 갱신 체인(onDeleted: 목록 재조회 + 마지막-삭제 처리)의 실패를
      // '삭제 실패'로 오인시키지 않도록 catch 를 분리한다 (#1319 — tokyo dev 835 루프 실측).
      try {
        await onDeleted?.(credentialId)
      } catch (postErr) {
        console.warn('[passkey] post-delete refresh failed:', postErr)
        errorBase({ title: t('appConfig.settings.passkeyListRefreshFail') })
      }
      onClose?.()
    } catch (err) {
      setError(t('passkey.error.generic'))
    } finally {
      setProcessing(false)
    }
  }

  return (
    <Modal
      open={open}
      onCancel={processing ? undefined : onClose}
      destroyOnClose
      width={480}
      className={styles.passkeyDeleteModalRoot}
      closable={false}
      maskClosable={!processing}
      footer={
        <>
          <Button className={styles.passkeyDeleteCancelBtn} onClick={onClose} disabled={processing}>
            {t('common.button.cancel', 'Cancel')}
          </Button>
          <Button
            className={classNames('type8', 'h38', 'w100')}
            type="primary"
            loading={processing}
            onClick={handleDelete}
            disabled={!masterPw}
          >
            {t('common.button.ok', 'OK')}
          </Button>
        </>
      }
    >
      <div className={styles.passkeyDeleteContent}>
        <img src={exclamationCircleRed22} alt="" className={styles.passkeyDeleteHeaderIcon} />
        <div className={styles.passkeyDeleteRight}>
          <p className={styles.passkeyDeleteTitle}>{t('passkey.deleteConfirmTitle')}</p>
          <div className={styles.passkeyDeleteContainer}>
            <p className={styles.passkeyDeleteDesc}>{t('passkey.deleteConfirmBody')}</p>
            <Input.Password
              id="passkey-delete-master-pw"
              className={styles.passkeyDeleteInput}
              value={masterPw}
              onChange={(e) => setMasterPw(e.target.value)}
              placeholder={t('passkey.masterPwPlaceholder')}
              disabled={processing}
              onPressEnter={handleDelete}
            />
            {error && <p className={styles.passkeyDeleteError}>{error}</p>}
          </div>
        </div>
      </div>
    </Modal>
  )
}

// React 18 에서 function component 의 defaultProps 가 deprecated 라
// 디스트럭처링 기본값으로 옮겼다. eslint react/require-default-props 가
// 그 패턴을 아직 인식 못해 위 block 전체에 룰 disable.
/* eslint-disable react/require-default-props */
PasskeyDeleteConfirmModal.propTypes = {
  open: PropTypes.bool,
  credentialId: PropTypes.string,
  // eslint-disable-next-line react/no-unused-prop-types
  deviceLabel: PropTypes.string, // 호환: 부모(ManagePasskeysModal)가 전달하지만 새 디자인에선 표시 안 함
  onClose: PropTypes.func,
  onDeleted: PropTypes.func,
}
/* eslint-enable react/require-default-props */

export default PasskeyDeleteConfirmModal
